import { useEffect, useState } from 'react';

import {
	EditSquareFilledIcon,
	EditSquareIcon,
	GovBuildingFilledIcon,
	GovBuildingIcon,
	HomeFilledIcon,
	HomeIcon,
	SpeakerFilledIcon,
	SpeakerIcon,
} from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import {
	ContentSection,
	MainContent,
	ResponsiveNavigation,
	ResponsiveNavigationItem,
	ResponsiveNavigationLayout,
} from '@change/ds-layout/next';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { MainContentTitle, ModuleSeparator } from 'src/app/pages/starterDashboardV2/shared/components';
import {
	DM_TAB_CLICK,
	HOME_TAB_CLICK,
	SHARE_TAB_CLICK,
	SUPPORTERS_TAB_CLICK,
} from 'src/app/pages/starterDashboardV2/shared/constants';
import {
	ButtonVariantProvider,
	SharingModalProvider,
	useDynamicModules,
} from 'src/app/pages/starterDashboardV2/shared/contexts';

import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

import { DecisionMakersList } from './components/DecisionMakersList';
import { RecentSupporters } from './components/EngageSupporters/components/RecentSupporters';
import { GrowPetition } from './components/GrowPetition';
import { NotificationsCenter } from './components/NotificationsCenter';
import { PageBanner } from './components/PageBanner';
import { PetitionInsights } from './components/PetitionInsights';
import { PetitionOverview } from './components/PetitionOverview';
import { ReadyToShare } from './components/ReadyToShare';
import { Recommendations } from './components/Recommendations';
import { StarterDashboardComments } from './components/StarterDashboardComments';
import { StepsToVictory } from './components/StepsToVictory';
import { UpdateSupporters } from './components/UpdateSupporters';

const TAB_CLICK_EVENTS: Record<string, string> = {
	home: HOME_TAB_CLICK,
	share: SHARE_TAB_CLICK,
	'decision-maker': DM_TAB_CLICK,
	updates: SUPPORTERS_TAB_CLICK,
};

const navigationItems = [
	{
		selectedIcon: <HomeFilledIcon size="small" />,
		icon: <HomeIcon size="small" />,
		id: 'home',
		desktopLabel: 'Home',
		mobileLabel: 'Home',
		ariaLabel: 'Navigate to Home',
		section: 'home',
	},
	{
		selectedIcon: <SpeakerFilledIcon size="small" />,
		icon: <SpeakerIcon size="small" />,
		id: 'share',
		desktopLabel: 'Share petition',
		mobileLabel: 'Sharing tools',
		ariaLabel: 'Navigate to Share your petition',
		section: 'share',
	},
	{
		selectedIcon: <GovBuildingFilledIcon size="small" />,
		icon: <GovBuildingIcon size="small" />,
		id: 'decision-maker',
		desktopLabel: 'Contact decision makers',
		mobileLabel: 'Decision maker',
		ariaLabel: 'Navigate to Connect to decision makers',
		section: 'decision-maker',
	},
	{
		selectedIcon: <EditSquareFilledIcon size="small" />,
		icon: <EditSquareIcon size="small" />,
		id: 'updates',
		desktopLabel: 'Post an update',
		mobileLabel: 'Post update',
		ariaLabel: 'Navigate to Message your supporters',
		section: 'updates',
	},
];

export function PageBodyReskin() {
	const { slug, section } = useKnownAvailableParams<{ slug: string; section: string }>();
	const baseUrl = `/p/${slug}/dashboard`;
	const track = useTracking();
	const [previous, setPrevious] = useState('home');

	useEffect(() => {
		if (previous === section) return;
		const event = TAB_CLICK_EVENTS[section];
		void track(event, { context: previous });
		window.scrollTo({ top: 0 });
	}, [section, previous, track]);

	const { isDynamicModuleLoaded } = useDynamicModules();
	const [commentsVisibility, setCommentsVisibility] = useState(true);

	// Get viewport information for responsive behavior
	const viewportInfo = useResponsiveLayout();

	return (
		<ButtonVariantProvider data-qa="button-variant-context">
			<SharingModalProvider>
				<ResponsiveNavigationLayout
					onTabChange={setPrevious}
					data-testid="page-body-reskin"
					viewportInfo={viewportInfo}
					selectedKey={section}
					defaultSelectedKey="home"
				>
					<ResponsiveNavigation ariaLabel="Main Application Navigation" className="xl:px-0">
						{navigationItems.map((item) => (
							<ResponsiveNavigationItem
								key={item.id}
								selectedIcon={item.selectedIcon}
								icon={item.icon}
								id={item.id}
								desktopLabel={item.desktopLabel}
								mobileLabel={item.mobileLabel}
								ariaLabel={item.ariaLabel}
								href={`${baseUrl}/${item.section}`}
							/>
						))}
					</ResponsiveNavigation>
					<MainContent>
						<PageBanner />
						<ContentSection id="home" className="flex flex-col">
							{/* The following title should only be visible in desktop */}
							<NotificationsCenter />
							<MainContentTitle title="Home" />
							<PetitionOverview />
							<ModuleSeparator />
							<Recommendations slug={slug} viewContext="home" />
							{isDynamicModuleLoaded('Recommendations') && (
								<ModuleSeparator data-testid="petition-overview-separator" />
							)}
							<div className={cn('flex flex-col', { 'lg:flex-row': commentsVisibility })}>
								<div className="flex-1">
									<RecentSupporters />
								</div>
								{commentsVisibility && (
									<>
										<div className="flex-0 min-w-[1px]">
											<ModuleSeparator className="block lg:hidden" />
											<ModuleSeparator orientation="vertical" className="hidden lg:block" />
										</div>
										<div className="flex-1">
											<StarterDashboardComments setCommentsVisibility={setCommentsVisibility} />
										</div>
									</>
								)}
							</div>
							{isDynamicModuleLoaded('PetitionInsights') && (
								<ModuleSeparator data-testid="petition-insights-separator" />
							)}
							<PetitionInsights />
						</ContentSection>

						<ContentSection id="share" className="flex flex-col">
							<MainContentTitle title="Share your petition" />
							<ReadyToShare />
							{isDynamicModuleLoaded('ReadyToShare') && <ModuleSeparator data-testid="ready-to-share-separator" />}
							<Recommendations slug={slug} viewContext="share" />
							{isDynamicModuleLoaded('Recommendations') && (
								<ModuleSeparator data-testid="share-recommendations-separator" />
							)}
							<GrowPetition />
							{isDynamicModuleLoaded('StepsToVictory') && <ModuleSeparator data-testid="steps-to-victory-separator" />}
							<StepsToVictory />
						</ContentSection>

						<ContentSection id="decision-maker" className="flex flex-col">
							<MainContentTitle title="Contact decision makers" />
							<DecisionMakersList />
						</ContentSection>
						<ContentSection id="updates" className="flex flex-col">
							<MainContentTitle title="Post an update" />
							<UpdateSupporters />
						</ContentSection>
					</MainContent>
				</ResponsiveNavigationLayout>
			</SharingModalProvider>
		</ButtonVariantProvider>
	);
}
